import { useRef, useMemo, useReducer, memo } from 'react';
import Autocomplete from '@dbh/autocomplete-input-lazy-loader';
import IntlFormField from '@dbh/intl-form-field';
import { Map, List, fromJS } from 'immutable';
import { SEARCH_CONTEXT_ID_SEARCH_TYPE } from '@dbh/admindev-constants';
import { ADMIN_IMAGES } from '@dbh/images-redux';
import { jsx } from 'react/jsx-runtime';
import _debounce from 'lodash/debounce';
import { usePreviousValue, useEventCallback } from '@dbh/react-hooks';
import { request } from '@dbh/request';
import 'prop-types';
import '@dbh/generic-types';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import 'react-immutable-proptypes';

const DEFAULT_OPTIONS={// @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent}
encoder:a=>encodeURIComponent(a).replace(/[!'()*]|%20/g,a=>"%20"===a?"+":"%"+a.charCodeAt(0).toString(16))};/**
 * Given an `apiUrl` an a user input creates an endpoint
 * where to load the suggestions.
 * Note: the `apiUrl` is expected to have a substring `((value))`
 * to be replaced with the user input `urlEncoded`.
 * @param {string} apiUrl Endpoint where we fetch the suggestions.
 * @param {string} value User input to escape.
 * @param {Object?} options .
 * @param {Function} options.encoder Encodes the value. Defaults to an `RFC 3986`
 * compliant encoder.
 * @param {number?} options.limit Limit to the suggestions.
 * @return {string} An endpoint where to fetch the suggestions.
 * @throws {Error} If `apiUrl` is not a string.
 * @throws {Error} If value is not a string.
 * @throws {Error} If encoder is not a function.
 * @throws {Error} If limit is defined but it is not a finite number.
 * @see {@link https://tools.ietf.org/html/rfc3986}
 */const buildApiUrl=withConformsTo("buildApiUrl",[],(a,b,c)=>{const{encoder:d,limit:e}={...DEFAULT_OPTIONS,...c},f=decodeURIComponent(a),g=d(b),h=f.replace("((value))",g),i=f.includes("((_limit))");if(!i)return h;if(e){const a=d(e);return h.replace("((_limit))",a)}return h.replace(/&?_limit=\(\(_limit\)\)/,"")});

const initialState=Map({// An `Immutable.Map` that stores each search result hit by its key (id).
hitsByKey:Map(),// An `Immutable.Map` that stores a list of search results hits by the
// search text that generated them.
hitsKeysBySearchText:Map(),// An `Immutable.List` that stores the current search result hits.
currentHits:null,// The current valid search text.
searchText:""});const INVALID_SEARCH_REQUESTED="INVALID_SEARCH_REQUESTED";const SEARCH_TEXT_CHANGED="SEARCH_TEXT_CHANGED";const SEARCH_RESULTS_RECEIVED="SEARCH_RESULTS_RECEIVED";

/*
 *
 * `getItemKey`: `@dbh/search-api-autocomplete-field`.
 *
 *//**
 * Get a value from the item props that can uniquely identify the item.
 * @param {Immutable.Map} item A single item from the item list passed to `Autocomplete`.
 * @return {string} .
 */const getItemKey=a=>a.get("type")+"-"+a.get("value");var getItemKey$1 = getItemKey;

/**
 * Selects the current search results hits from the state, using the current text and
 * the cache.
 * @param {Immutable.Map} state .
 * @return {Immutable.List} The current search results.
 */const selectCurrentHits=a=>{// Gets the list of keys corresponding to the current search text.
const b=a.getIn(["hitsKeysBySearchText",a.get("searchText")]);// If the list of keys doesn't exist, return the previous results.
if(!b)return a.get("currentHits");// Otherwise resolve the keys to the original search results that are stored
// in the `hitsByKey` `Map`.
const c=b.map(b=>a.getIn(["hitsByKey",b]));return c};/**
 * @typedef {Immutable.List} SearchResultsKeys .
 * @typedef {Immutable.Map} SearchResultsBykeys .
 * @typedef {Array<SearchResultsKeys,SearchResultsBykeys>} NormalizedSearchResults .
 *//**
 * Normalizes a list of search results in a list of keys and a map
 * of search results, that can be easily merged and accessed in the
 * reducer state.
 * @param {Immutable.List} results The transformed search api results.
 * @return {NormalizedSearchResults} .
 */const normalizeSearchResults=a=>{const{resultsByKey:b,resultsKeys:c}=a.reduce((a,b)=>{const c=getItemKey$1(b);return a.resultsByKey=a.resultsByKey.set(c,b),a.resultsKeys=a.resultsKeys.push(c),a},{resultsKeys:List(),resultsByKey:Map()});return [c,b]};

/**
 * Dispatched on an invalid search request.
 * @param {string} searchText .
 * @return {Object} .
 */const invalidSearchRequested=a=>({type:INVALID_SEARCH_REQUESTED,payload:{searchText:a}});/**
 * Dispatched on a search text change.
 * @param {string} searchText .
 * @return {Object} .
 */const searchTextChanged=a=>({type:SEARCH_TEXT_CHANGED,payload:{searchText:a}});/**
 * Dispatched when a search request returns with results.
 * @param {string} searchText The search text that originated the request.
 * @param {Immutable.List} results .
 * @return {Object} .
 */const searchResultsReceived=(a,b)=>({type:SEARCH_RESULTS_RECEIVED,payload:{searchText:a,results:b}});

/**
 * Handles an `INVALID_SEARCH_REQUESTED` event.
 * Set the state search text to the current search text and the search hits to `null`.
 * @param {Immutable.Map} state .
 * @param {Object} event .
 * @param {Object} event.payload .
 * @param {string} event.payload.searchText .
 * @return {Immutable.Map} The next state.
 */const handleInvalidSearchRequested=(a,b)=>{let{payload:{searchText:c}}=b;return a.merge({searchText:c,currentHits:null})},handleSearchTextChanged=(a,b)=>{let{payload:{searchText:c}}=b;return a.withMutations(a=>{a.set("searchText",c),a.set("currentHits",selectCurrentHits(a));})},handleSearchResultsReceived=(a,b)=>{let{payload:{searchText:c,results:d}}=b;const[e,f]=normalizeSearchResults(d);return a.withMutations(a=>{a// Set the list of results keys to the search text that originated the search.
.setIn(["hitsKeysBySearchText",c],e)// Merge the results with the previous ones.
.mergeDeepIn(["hitsByKey"],f),a.set("currentHits",selectCurrentHits(a));})};/**
 * Handles a `SEARCH_TEXT_CHANGED` event.
 * Sets the current search text in the state and updates the corresponding hits
 * using the `selectCurrentHits` selector.
 * @param {Immutable.Map} state .
 * @param {Object} event .
 * @param {Object} event.payload .
 * @param {string} event.payload.searchText .
 * @return {Immutable.Map} The next state.
 *//**
 * Handles a `SEARCH_RESULTS_RECEIVED` event.
 * Normalizes the search results and stores them in the state.
 * @param {Immutable.Map} state .
 * @param {Object} event .
 * @param {Object} event.payload .
 * @param {string} event.payload.searchText .
 * @param {Object} event.payload.results .
 * @return {Immutable.Map} The next state.
 *//**
 * The local reducer for the `useSearchApi` hook.
 * @param {Immutable.Map} state .
 * @param {Object} event .
 * @return {Immutable.Map} The next state.
 */const searchLayerReducer=(a,b)=>{switch(b.type){case INVALID_SEARCH_REQUESTED:return handleInvalidSearchRequested(a,b);case SEARCH_TEXT_CHANGED:return handleSearchTextChanged(a,b);case SEARCH_RESULTS_RECEIVED:return handleSearchResultsReceived(a,b);default:return a}};

/*
 *
 * Constants: `useSearchApi`.
 *
 */const SEARCH_DEBOUNCE_RATE=350;const DEFAULT_MINIMUM_SEARCH_CHARACTERS=3;const DEFAULT_LIMIT=3;

/**
 * Compares two lists of search hits to determine if they are equal.
 * Two lists are considered equal if they have the same size and contain
 * the same items in the same order, where equality is determined by comparing
 * the 'value' property of each hit.
 * @param {Immutable.List} firstHits First list of search hits to compare.
 * @param {Immutable.List} second Second list of search hits to compare.
 * @return {boolean} True if the lists are equal, false otherwise.
 * @example
 * const hits1 = List([
 *   Map({
 *     searchText: "Rome",
 *     additionalValues: {},
 *     idSearchType: 1,
 *     iconSrcReference: {
 *       reference: "location-pin",
 *       firstColor: "blackMid"
 *     },
 *     type: "cities",
 *     value: 1
 *   })
 * ]);
 * const hits2 = List([
 *   Map({
 *     // Different object but same value
 *     value: 1
 *   })
 * ]);
 * areHitsEqual(hits1, hits2); // Returns true
 */const areHitsEqual=withConformsTo("areHitsEqual",[],(a,b)=>a.size===b.size&&a.every((a,c)=>{const d=b.get(c);return a.get("value")===d.get("value")}));var areHitsEqual$1 = areHitsEqual;

/**
 * A hook that memoizes a value using deep equality comparison to avoid
 * unnecessary re-renders. Similar to React's useMemo but uses Immutable.js's
 * `is()` for deep comparison. Returns the previous value if it is deeply equal
 * to the new value.
 * @param {Immutable.List?} value The value to memoize.
 * @param {boolean} canMemoize Whether to memoize the value.
 * @return {Immutable.List?} The memoized value.
 */const useMemoizeSearchHits=withConformsTo("useMemoizeSearchHits",[],(a,b)=>{const c=useRef();return useMemo(()=>a?b&&c.current&&areHitsEqual$1(c.current,a)?c.current:(c.current=a,a):a,[a,b])});var useMemoizeSearchHits$1 = useMemoizeSearchHits;

const useSearchApi=a=>{let{transformResponse:b,apiUrl:c,minimumSearchCharacters:e=DEFAULT_MINIMUM_SEARCH_CHARACTERS,limit:f=DEFAULT_LIMIT,onLoadAutocompleteSuggestions:d}=a;const[g,h]=useReducer(searchLayerReducer,initialState),i=g.get("searchText"),j=usePreviousValue(i),k=useMemoizeSearchHits$1(g.get("currentHits"),(null==i?void 0:i.toLowerCase())===(null==j?void 0:j.toLowerCase())),l=g.get("hitsKeysBySearchText"),m=useMemo(()=>_debounce(a=>{const e=buildApiUrl(c,a,{limit:f});request(e).then(c=>{const e=b?b(c):c;h(searchResultsReceived(a,e)),d&&d({searchText:a,hits:e});}).catch(()=>{h(invalidSearchRequested());});},SEARCH_DEBOUNCE_RATE,{leading:!0}),[c,b,f,d]),n=useEventCallback(/**
     * Starts a search if the requested `searchText` meets the
     * `minimumSearchCharacters`.
     * This updates the internal `searchText` state and calls
     * the debounced `onValidSearch` method.
     * @param {string} searchText .
     */a=>{if(!a||a.length<e)return void h(invalidSearchRequested(a));h(searchTextChanged(a));l.get(a)||m(a);},[e,l,m]);// A map of the previous search hits keys by search text.
// @example {rome: [1, 8209, 8208, 4458, 3233], milan: [2, 10195, 2589, 5148]}
return [k,n]};var useSearchApi$1 = useSearchApi;

/*
 *
 * `shouldItemUpdate`: `@dbh/search-api-autocomplete-field`.
 *
 *//**
 * This will be checked in the `Item` component `shouldComponentUpdate` methods
 * as a way to decide if the component should update or not, granting a small
 * performance benefit if the items can be differentiated by unique keys.
 * @param {Immutable.Map} item The current item in the `Item` component.
 * @param {any} nextItem The item passed for the next render of the
 * `Item` component.
 * @return {boolean} Should the component update.
 */const shouldItemUpdate=(a,b)=>a.get("value")!==b.get("value");var shouldItemUpdate$1 = shouldItemUpdate;

/**
 * Normalizes a value to a format that is compatible with the `Autocomplete` component.
 * This helper is used as the `normalize` `prop` of the `IntlFormField` that renders
 * the `Autocomplete` component. The main purpose is to convert plain text values into
 * an `Immutable.Map` that follows the specifications for a free text search item.
 * @param {string|Immutable.Map} value .
 * @param {string|Immutable.Map} previousAutoCompleteValue .
 * @return {Immutable.Map} .
 */const normalizeValueToAutocompleteItem=(a,b)=>{// If it's already an `Immutable.Map`, no need to normalize just return it.
if(Map.isMap(a))return a;if(Map.isMap(b)){const c=b.get("searchText");if(c===a)// In order to maintain the search type.
return b}// The other case is a string (or other primitive value) that was directly
// inputted in the text input, in this case the value will be returned as an
// `Immutable.Map` with an `idSearchType` that indicates that it's a free
// text search.
return Map({idSearchType:SEARCH_CONTEXT_ID_SEARCH_TYPE.FREE_TEXT,searchText:a,value:a})};var normalizeValueToAutocompleteItem$1 = normalizeValueToAutocompleteItem;

/**
 * Returns a string representation from a given item. This method is made to fit
 * the specs of the `itemToString` prop of the `Autocomplete` component.
 * It's used to get the string that have to be shown on the `Autocomplete` list and
 * in the input, for each item.
 * @param {Immutable.Map} item .
 * @return {string} .
 */const itemToString=a=>a?Map.isMap(a)?a.get("searchText")||"":a:"";var itemToString$1 = itemToString;

const{HOTEL,LOCATION_PIN}=ADMIN_IMAGES;const searchKeysProps=Map({cities:{idSearchType:SEARCH_CONTEXT_ID_SEARCH_TYPE.CITY,groupLabel:"components.Autocomplete.city",groupIconSrcReference:{reference:LOCATION_PIN,firstColor:"blackMid"}},hotels:{idSearchType:SEARCH_CONTEXT_ID_SEARCH_TYPE.HOTEL,groupLabel:"components.Autocomplete.hotel",groupIconSrcReference:{reference:HOTEL,firstColor:"blackMid"}},pois:{idSearchType:SEARCH_CONTEXT_ID_SEARCH_TYPE.POI,groupLabel:"components.Autocomplete.poi",groupIconSrcReference:{reference:LOCATION_PIN,firstColor:"blackMid"}},addresses:{idSearchType:SEARCH_CONTEXT_ID_SEARCH_TYPE.PLACE_ID,groupLabel:"components.Autocomplete.address",groupIconSrcReference:{reference:LOCATION_PIN,firstColor:"blackMid"}}});const searchKeyOrder=["cities","pois","addresses","hotels"];

/**
 * Extracts and normalizes the extra data from a search hit. Our search `API`  will
 * return different types of results in separated groups and some groups have additional
 * properties for each hit that will be used in the application.
 * @param {string} key The key of the group containing the hit.
 * @param {Immutable.Map} hitSource The "source" property of the search hit data.
 * @param {string?} hitSource.urlHotel Only for hits of type 'hotel'.
 * @param {string?} hitSource.lat Only for hits of type 'poi'.
 * @param {string?} hitSource.lng Only for hits of type 'poi'.
 * @return {Immutable.Map} The additional data corrisponding to the search hit.
 */const getHitAdditionalValues=(a,b)=>{let c=Map();const d=searchKeysProps.getIn([a,"idSearchType"]);return d===SEARCH_CONTEXT_ID_SEARCH_TYPE.HOTEL?c=Map({urlHotel:b.get("url"),city:b.get("city")}):d===SEARCH_CONTEXT_ID_SEARCH_TYPE.POI&&(c=Map({locationDetails:Map({latitude:b.get("lat"),longitude:b.get("lng")})})),c};var getHitAdditionalValues$1 = getHitAdditionalValues;

/**
 * Transform a search `API` result into a list of items that can be used
 * with the `AutoComplete` component.
 * @param {Object} rawApiResponse The `API` response.
 * @return {Immutable.List} Containing the data of each item.
 */const makeAutocompleteItemsFromApiResponse=a=>{const{apiResponse:b}=a;let c=List();const d=fromJS(b),e=d.get("hydra:member");return searchKeyOrder.forEach(a=>{const b=e.find(b=>b.get("key")===a);if(!b)return;const d=b.getIn(["topGroupedResults","hits","hits"]);if(!d.size)return;const f=b.get("key");c=c.concat(d.map(a=>{const b=a.get("source"),c=b.get("id"),d=b.get("name"),e=Map({searchText:d,additionalValues:getHitAdditionalValues$1(f,b),idSearchType:searchKeysProps.getIn([f,"idSearchType"]),iconSrcReference:searchKeysProps.getIn([f,"groupIconSrcReference"]),type:f});return "addresses"===f?e.merge({placeId:c,value:c}):e.set("value",+c)}));}),c};var makeAutocompleteItemsFromApiResponse$1 = makeAutocompleteItemsFromApiResponse;

const SearchBoxAutocomplete=/*#__PURE__*/memo(a=>{let{apiUrl:b,onLoadAutocompleteSuggestions:c,minimumSearchCharacters:d,limit:e,...f}=a;const[g,h]=useSearchApi$1({transformResponse:makeAutocompleteItemsFromApiResponse$1,apiUrl:b,minimumSearchCharacters:d,limit:e,onLoadAutocompleteSuggestions:c});return/*#__PURE__*/jsx(IntlFormField/* `IntlFormField` props. */,{ForComponent:Autocomplete,format:itemToString$1,normalize:normalizeValueToAutocompleteItem$1,intlFormatter:!0/* `Autocomplete` props. */,onInputValueChange:h,items:g,itemToString:itemToString$1,shouldItemUpdate:shouldItemUpdate$1,getItemKey:getItemKey$1,...f})});SearchBoxAutocomplete.displayName="SearchBoxAutocomplete";

export { SearchBoxAutocomplete as default };
